import { useTable, useSortBy } from 'react-table';
import { useEffect, useMemo } from 'react';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';

import { formatDateCell } from '../../helpers/reactTable';
import TableButton from '../inputs/TableButton';
import { useMutation } from '@apollo/client';
import { DELETE_MAINTENANCE, END_MAINTENANCE } from '../../mutations';
import { MAINTENANCES_QUERY } from '../../queries';
import { useActionModal } from '../../hooks/useActionModal';
import { SET_SUCCESS } from '../../reducers/actionReducer';
import { dateIsInPast } from '../../utils';

export default function MaintenancesTable({ maintenances }) {
  const { dispatch: dispatchAction } = useActionModal();

  const [
    endMaintenance,
    { loading: loadingEndMaintenance, error: errorEndMaintenance },
  ] = useMutation(END_MAINTENANCE, {
    refetchQueries: [MAINTENANCES_QUERY],
    onCompleted: (data) => {
      dispatchAction({
        type: SET_SUCCESS,
        payload: {
          message: data.endMaintenance?.message,
        },
      });
    },
  });
  const [
    deleteMaintenance,
    { loading: loadingDeleteMaintenance, error: errorDeleteMaintenance },
  ] = useMutation(DELETE_MAINTENANCE, {
    refetchQueries: [MAINTENANCES_QUERY],
    onCompleted: (data) => {
      dispatchAction({
        type: SET_SUCCESS,
        payload: {
          message: data.deleteMaintenance?.message,
        },
      });
    },
  });

  const loading = loadingEndMaintenance || loadingDeleteMaintenance;
  const error = errorEndMaintenance || errorDeleteMaintenance;

  useEffect(() => {
    if (error) {
      dispatchAction({
        type: SET_SUCCESS,
        payload: {
          message: error.message,
        },
      });
    }
  }, [dispatchAction, error]);

  const data = useMemo(() => maintenances, [maintenances]);
  const columns = useMemo(
    () => [
      {
        Header: 'Start Date',
        accessor: 'startDate',
        Cell: formatDateCell,
      },
      {
        Header: 'End Date',
        accessor: 'endDate',
        Cell: formatDateCell,
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => (
          <div className="flex items-center space-x-2 whitespace-nowrap">
            <TableButton
              onClick={() =>
                endMaintenance({ variables: { id: row.original.id } })
              }
              disabled={
                loading ||
                (row.original?.endDate && dateIsInPast(row.original?.endDate))
              }
            >
              End Now
            </TableButton>
            <TableButton
              onClick={() =>
                deleteMaintenance({ variables: { id: row.original.id } })
              }
              disabled={loading}
            >
              Delete
            </TableButton>
          </div>
        ),
      },
    ],
    [deleteMaintenance, endMaintenance, loading]
  );

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useSortBy
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div className="h-full w-full overflow-auto pb-14">
      <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border p-2 text-left hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {column.render('Header')}
                    {column.isSorted && (
                      <span className="pl-2">
                        {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} className="border p-2">
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
